/**
 * DuplicateReviewDialog — Revue manuelle des doublons potentiels
 *
 * Comportement STRICT :
 *  - Aucune fusion automatique. Chaque paire attend une décision explicite
 *    de l'utilisateur (Fusionner / Ce ne sont pas des doublons / Examiner
 *    plus tard).
 *  - Lors d'un « Fusionner », l'utilisateur choisit quelle fiche conserver
 *    comme référence principale (radio a/b).
 *  - « Ce ne sont pas des doublons » enregistre la décision côté serveur
 *    (`POST /api/duplicates/dismiss`) → la paire ne sera plus proposée.
 *  - « Examiner plus tard » ferme simplement la paire sans mémoriser.
 *  - Récap live en pied de dialog : fusionnées / refusées / en attente.
 *  - Bouton « Annuler la dernière fusion » restaure la fiche depuis l'audit.
 *
 * Le dialog est agnostique du domaine — même composant réutilisé pour
 * `products` et `suppliers` via la prop `kind`.
 */
import { useState } from "react";
import { AlertTriangle, CheckCircle2, XCircle, Clock, Undo2, Info, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { api } from "../lib/api";
import { eur, dateFR } from "../lib/format";

const KIND_LABELS = {
  product:  { singular: "produit",     plural: "produits" },
  supplier: { singular: "fournisseur", plural: "fournisseurs" },
};

export default function DuplicateReviewDialog({ open, kind, pairs, onClose, onCompleted }) {
  // decisions[pair_id] = {status, keepId?, auditId?} — status: pending|merging|merged|dismissed|later|error
  const [decisions, setDecisions] = useState({});
  const [keepPick, setKeepPick] = useState({});  // pair_id → id à conserver (défaut : le plus ancien)
  const label = KIND_LABELS[kind] || KIND_LABELS.product;

  const setDec = (pid, patch) => setDecisions((d) => ({ ...d, [pid]: { ...(d[pid] || {}), ...patch } }));

  const merge = async (pair) => {
    const keep = keepPick[pair.pair_id] || pickDefaultKeep(pair);
    const drop = keep === pair.a.id ? pair.b : pair.a;
    setDec(pair.pair_id, { status: "merging" });
    try {
      let res;
      if (kind === "product") {
        res = await api.mergeProduct(drop.id, keep);
      } else {
        res = await api.mergeSuppliers(drop.id, keep, null);
      }
      setDec(pair.pair_id, { status: "merged", auditId: res.audit_id, keepId: keep, dropId: drop.id });
      toast.success(`« ${drop.name} » fusionné dans « ${(keep === pair.a.id ? pair.a : pair.b).name} »`);
    } catch (e) {
      setDec(pair.pair_id, { status: "error" });
      toast.error(e?.response?.data?.detail || "Fusion impossible");
    }
  };

  const dismiss = async (pair) => {
    setDec(pair.pair_id, { status: "dismissing" });
    try {
      await api.dismissDuplicate(kind, pair.a.id, pair.b.id);
      setDec(pair.pair_id, { status: "dismissed" });
      toast.success("Décision enregistrée — cette paire ne sera plus proposée");
    } catch {
      setDec(pair.pair_id, { status: "pending" });
      toast.error("Impossible d'enregistrer le refus");
    }
  };

  const later = (pair) => setDec(pair.pair_id, { status: "later" });

  const undoMerge = async (pair) => {
    const dec = decisions[pair.pair_id];
    if (!dec?.auditId) return;
    setDec(pair.pair_id, { ...dec, status: "undoing" });
    try {
      if (kind === "product") await api.unmergeProduct(dec.auditId);
      else await api.unmergeSupplier(dec.auditId);
      setDec(pair.pair_id, { status: "pending" });
      toast.success("Fusion annulée — fiche restaurée");
    } catch {
      setDec(pair.pair_id, { ...dec, status: "merged" });
      toast.error("Impossible d'annuler la fusion");
    }
  };

  const counts = pairs.reduce((acc, p) => {
    const s = decisions[p.pair_id]?.status || "pending";
    if (s === "merged") acc.merged += 1;
    else if (s === "dismissed") acc.dismissed += 1;
    else if (s === "later") acc.later += 1;
    else acc.pending += 1;
    return acc;
  }, { merged: 0, dismissed: 0, later: 0, pending: 0 });

  const close = () => {
    // Signale un rechargement au parent pour reconstruire la liste (côté prod/suppliers).
    onCompleted?.({ counts });
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-[980px] w-[95vw] p-0 gap-0 max-h-[90vh] flex flex-col" data-testid={`duplicate-review-dialog-${kind}`}>
        <DialogHeader className="px-5 py-4 border-b border-[#CDD7E3] bg-[#F4F5EF]">
          <DialogTitle className="flex items-center gap-2 text-[15px]">
            <AlertTriangle className="w-4 h-4 text-[#B36B00]" />
            Nettoyer les doublons · {pairs.length} {label.plural} suspects
          </DialogTitle>
          <p className="text-[12px] text-[#5E6875] mt-1">
            Aucune fusion n'est effectuée sans votre validation explicite pour chaque paire.
            Nous prenons également en compte la marque, le format, le volume, l'unité, la catégorie
            et les données des factures — pas uniquement les noms.
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid={`duplicate-review-list-${kind}`}>
          {pairs.length === 0 ? (
            <div className="text-center py-12 text-[13px] text-[#5E6875]">
              Aucun doublon potentiel détecté. Rien à revoir pour l'instant.
            </div>
          ) : pairs.map((pair) => (
            <PairCard
              key={pair.pair_id}
              pair={pair}
              kind={kind}
              decision={decisions[pair.pair_id]}
              keepId={keepPick[pair.pair_id] || pickDefaultKeep(pair)}
              onKeepPick={(id) => setKeepPick((k) => ({ ...k, [pair.pair_id]: id }))}
              onMerge={() => merge(pair)}
              onDismiss={() => dismiss(pair)}
              onLater={() => later(pair)}
              onUndo={() => undoMerge(pair)}
            />
          ))}
        </div>

        {/* Récap + fermeture */}
        <div className="px-5 py-3 border-t border-[#CDD7E3] bg-white flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-3 text-[12px] font-mono-num" data-testid="duplicate-review-summary">
            <Summary icon={<CheckCircle2 className="w-3.5 h-3.5 text-[#24845B]" />} k="Fusionnés" v={counts.merged} testid="sum-merged" />
            <Summary icon={<XCircle     className="w-3.5 h-3.5 text-[#5E6875]" />} k="Refusés"   v={counts.dismissed} testid="sum-dismissed" />
            <Summary icon={<Clock       className="w-3.5 h-3.5 text-[#B36B00]" />} k="En attente" v={counts.later + counts.pending} testid="sum-later" />
          </div>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" onClick={close} data-testid="duplicate-review-close">Fermer</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function pickDefaultKeep(pair) {
  // Défaut : plus ancien created_at OU celui avec le plus de factures liées.
  const a = pair.a, b = pair.b;
  const aInv = a.invoices_count || 0, bInv = b.invoices_count || 0;
  if (aInv !== bInv) return aInv > bInv ? a.id : b.id;
  const aDate = a.created_at || "", bDate = b.created_at || "";
  return aDate <= bDate ? a.id : b.id;
}

function Summary({ icon, k, v, testid }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[#0B1F33]" data-testid={testid}>
      {icon}
      <span className="font-semibold">{k}</span>
      <span className="font-bold tabular-nums">{v}</span>
    </span>
  );
}

function PairCard({ pair, kind, decision, keepId, onKeepPick, onMerge, onDismiss, onLater, onUndo }) {
  const status = decision?.status || "pending";
  const isMerged = status === "merged";
  const isDismissed = status === "dismissed";
  const isLater = status === "later";
  const isBusy = status === "merging" || status === "dismissing" || status === "undoing";
  const closed = isMerged || isDismissed || isLater;
  return (
    <div
      className={`rounded-xl border p-4 transition-colors ${
        isMerged     ? "border-[#C0E1CC] bg-[#E4F1EB]/60"
        : isDismissed ? "border-[#CDD7E3] bg-[#F4F5EF]/60"
        : isLater    ? "border-[#F1D9A8] bg-[#FFF7E9]/60"
        : "border-[#CDD7E3] bg-white"
      }`}
      data-testid={`duplicate-pair-${pair.pair_id}`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2 text-[11px] font-semibold text-[#5E6875] flex-wrap">
          <TierBadge tier={pair.tier} />
          <span className="mx-1">·</span>
          Confiance {Math.round(pair.score * 100)}%
          <span className="mx-1">·</span>
          {pair.reasons.join(" · ")}
        </div>
        {isMerged && (
          <Button size="sm" variant="ghost" onClick={onUndo} disabled={isBusy}
            data-testid={`duplicate-undo-${pair.pair_id}`}
            className="h-8 text-[#0B1F33] hover:bg-[#F4F5EF]">
            <Undo2 className="w-3.5 h-3.5 mr-1" /> Annuler la fusion
          </Button>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {["a", "b"].map((side) => {
          const item = pair[side];
          const isKeep = item.id === keepId;
          return (
            <button
              key={side}
              type="button"
              disabled={closed}
              onClick={() => onKeepPick(item.id)}
              data-testid={`duplicate-pick-${pair.pair_id}-${side}`}
              className={`text-left rounded-lg border p-3 transition-colors ${
                closed
                  ? "border-[#CDD7E3] bg-white opacity-70 cursor-default"
                  : isKeep
                  ? "border-[#FF9F1C] bg-[#FFF7E9] shadow-[0_2px_6px_rgba(255,159,28,0.2)]"
                  : "border-[#CDD7E3] bg-white hover:bg-[#F4F5EF]"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-heading font-bold text-[14.5px] text-[#0B1F33] truncate">{item.name || "—"}</span>
                {!closed && (
                  <Badge className={`text-[10px] ${isKeep ? "bg-[#FF9F1C] text-[#0B1F33] border-[#FF9F1C]" : "bg-white text-[#5E6875] border-[#CDD7E3]"}`}>
                    {isKeep ? "Référence principale" : "Choisir comme référence"}
                  </Badge>
                )}
              </div>
              <div className="mt-2 grid grid-cols-2 gap-1.5 text-[11.5px]">
                {kind === "product" ? (
                  <>
                    <Field k="Catégorie"       v={item.category} />
                    <Field k="Marque"          v={item.brand} />
                    <Field k="Fournisseur"     v={item.supplier_name} />
                    <Field k="Réf. fournisseur" v={item.supplier_ref} />
                    <Field k="Contenance"      v={item.content_value && item.content_unit ? `${item.content_value} ${item.content_unit}` : null} />
                    <Field k="Cond. achat"     v={`${item.purchase_quantity ?? "—"} ${item.purchase_unit || ""}${item.pack_units > 1 ? ` × ${item.pack_units}` : ""}`} />
                    <Field k="EAN"             v={item.ean} mono />
                    <Field k="Prix actuel"     v={item.purchase_price != null ? eur(item.purchase_price, 3) : null} mono />
                    <Field k="Factures liées"  v={item.invoices_count} mono />
                    <Field k="Dernier achat"   v={dateFR(item.last_purchase_date)} />
                    <Field k="Dernier prix"    v={item.last_price_ht != null ? eur(item.last_price_ht, 3) : null} mono />
                  </>
                ) : (
                  <>
                    <Field k="Raison sociale"  v={item.legal_name} />
                    <Field k="SIRET"           v={item.siret} mono />
                    <Field k="SIREN"           v={item.siren} mono />
                    <Field k="N° TVA"          v={item.vat_number} mono />
                    <Field k="Adresse"         v={item.address} />
                    <Field k="Site (indice)"   v={item.site_hint} />
                    <Field k="Factures liées"  v={item.invoices_count} mono />
                    <Field k="Dernière facture" v={dateFR(item.last_invoice_date)} />
                  </>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {!closed ? (
        <div className="mt-3 flex gap-2 flex-wrap">
          <Button
            onClick={onMerge}
            disabled={isBusy}
            data-testid={`duplicate-merge-${pair.pair_id}`}
            className="btn-orange border-0 !h-9"
          >
            {isBusy && status === "merging" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
            Fusionner
          </Button>
          <Button
            variant="outline"
            onClick={onDismiss}
            disabled={isBusy}
            data-testid={`duplicate-dismiss-${pair.pair_id}`}
            className="!h-9"
          >
            <XCircle className="w-3.5 h-3.5 mr-1" /> Ce ne sont pas des doublons
          </Button>
          <Button
            variant="ghost"
            onClick={onLater}
            disabled={isBusy}
            data-testid={`duplicate-later-${pair.pair_id}`}
            className="!h-9 text-[#5E6875]"
          >
            <Clock className="w-3.5 h-3.5 mr-1" /> Examiner plus tard
          </Button>
        </div>
      ) : (
        <div className="mt-3 text-[12px] font-semibold" data-testid={`duplicate-status-${pair.pair_id}`}>
          {isMerged     && <span className="text-[#24845B]">✓ Fusionné — référence conservée : {(keepId === pair.a.id ? pair.a : pair.b).name}</span>}
          {isDismissed  && <span className="text-[#5E6875]">Refusé — ne sera plus proposé</span>}
          {isLater      && <span className="text-[#B36B00]">Reporté</span>}
        </div>
      )}
    </div>
  );
}

function Field({ k, v, mono }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-[#5E6875]">{k}</span>
      <span className={`text-[#0B1F33] font-semibold truncate ${mono ? "font-mono-num tabular-nums" : ""}`}>
        {v || v === 0 ? v : "—"}
      </span>
    </div>
  );
}

/**
 * Badge indiquant la fiabilité du rapprochement — pilote la lecture visuelle :
 *  - « certain » : rattachement sûr (EAN valide, EAN+ref, ref+fournisseur) → vert
 *  - « probable » : nécessite validation utilisateur (ref cross-fournisseur, similarité sémantique) → orange
 * Rendu inline dans l'en-tête de la carte, à côté de la confiance.
 */
function TierBadge({ tier }) {
  if (tier === "certain") {
    return (
      <span
        data-testid="duplicate-tier-certain"
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#DCF3E7] text-[#24845B] border border-[#B7E1C7]"
      >
        <CheckCircle2 className="w-3 h-3" /> Correspondance certaine
      </span>
    );
  }
  return (
    <span
      data-testid="duplicate-tier-probable"
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#FFF7E9] text-[#B36B00] border border-[#F1D9A8]"
    >
      <Info className="w-3 h-3" /> À valider
    </span>
  );
}
